import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
	Card, CardContent, CardHeader, CardTitle, CardDescription,
	Tabs, TabsContent, TabsList, TabsTrigger,
	Button, Input, Label, useToast
} from '@hiring-intelligence/ui';
import { sourcingApi, CandidateVaultDetail } from '@/services/api';
import { Search, Linkedin, UserPlus, Loader2 } from 'lucide-react';

interface AddCandidateSectionProps {
	hiringRequestId: string;
	onCandidateAdded: () => void;
}

type VaultSearchResult = Pick<CandidateVaultDetail, 'id' | 'signalhireId' | 'fullName' | 'currentTitle' | 'currentCompany' | 'location' | 'photoUrl'>;

export function AddCandidateSection({ hiringRequestId, onCandidateAdded }: AddCandidateSectionProps) {
	const { toast } = useToast();
	const [isOpen, setIsOpen] = useState(false);

	// --- Vault Search State ---
	const [vaultQuery, setVaultQuery] = useState('');
	const [vaultResults, setVaultResults] = useState<VaultSearchResult[]>([]);
	const [isSearching, setIsSearching] = useState(false);

	// Debounced vault search
	useEffect(() => {
		if (vaultQuery.length < 2) {
			setVaultResults([]);
			return;
		}

		const timeout = setTimeout(async () => {
			setIsSearching(true);
			try {
				const res = await sourcingApi.searchVault(vaultQuery);
				setVaultResults(res.data || []);
			} catch {
				setVaultResults([]);
			} finally {
				setIsSearching(false);
			}
		}, 400);

		return () => clearTimeout(timeout);
	}, [vaultQuery]);

	const addFromVaultMutation = useMutation({
		mutationFn: (signalhireId: string) => sourcingApi.addFromVault(hiringRequestId, signalhireId),
		onSuccess: (res) => {
			toast({
				title: res.message ? 'Already in Pipeline' : 'Candidate Added',
				description: res.message || 'Candidate has been added to the pipeline.'
			});
			onCandidateAdded();
		},
		onError: (err: Error) => {
			toast({ title: 'Error', description: err.message, variant: 'destructive' });
		}
	});

	// --- LinkedIn State ---
	const [linkedinUrl, setLinkedinUrl] = useState('');

	const addByLinkedInMutation = useMutation({
		mutationFn: () => sourcingApi.addByLinkedIn(hiringRequestId, linkedinUrl),
		onSuccess: (res) => {
			toast({
				title: res.message ? 'Already in Pipeline' : 'Candidate Added',
				description: res.message || 'Candidate added. Profile lookup has started and will update automatically.'
			});
			setLinkedinUrl('');
			onCandidateAdded();
		},
		onError: (err: Error) => {
			toast({ title: 'Error', description: err.message, variant: 'destructive' });
		}
	});

	// --- Manual Add State ---
	const [manualForm, setManualForm] = useState({
		name: '',
		currentTitle: '',
		currentCompany: '',
		location: '',
		email: '',
		phone: '',
		linkedinUrl: ''
	});

	const addManualMutation = useMutation({
		mutationFn: () => sourcingApi.addManualCandidate(hiringRequestId, manualForm),
		onSuccess: () => {
			toast({ title: 'Candidate Added', description: 'Manual candidate has been added to the pipeline.' });
			setManualForm({ name: '', currentTitle: '', currentCompany: '', location: '', email: '', phone: '', linkedinUrl: '' });
			onCandidateAdded();
		},
		onError: (err: Error) => {
			toast({ title: 'Error', description: err.message, variant: 'destructive' });
		}
	});

	if (!isOpen) {
		return (
			<Button variant="outline" className="w-full" onClick={() => setIsOpen(true)}>
				<UserPlus className="h-4 w-4 mr-2" />
				Add Candidate Manually
			</Button>
		);
	}

	return (
		<Card>
			<CardHeader className="pb-3">
				<div className="flex items-center justify-between">
					<div>
						<CardTitle className="text-base">Add Candidate</CardTitle>
						<CardDescription>Search existing vault, look up by LinkedIn, or add manually</CardDescription>
					</div>
					<Button variant="ghost" size="sm" onClick={() => setIsOpen(false)}>✕</Button>
				</div>
			</CardHeader>
			<CardContent>
				<Tabs defaultValue="vault">
					<TabsList className="w-full">
						<TabsTrigger value="vault" className="flex-1">
							<Search className="h-3.5 w-3.5 mr-1.5" />
							Search Vault
						</TabsTrigger>
						<TabsTrigger value="linkedin" className="flex-1">
							<Linkedin className="h-3.5 w-3.5 mr-1.5" />
							LinkedIn
						</TabsTrigger>
						<TabsTrigger value="manual" className="flex-1">
							<UserPlus className="h-3.5 w-3.5 mr-1.5" />
							Manual
						</TabsTrigger>
					</TabsList>

					{/* Search Vault Tab */}
					<TabsContent value="vault" className="space-y-3">
						<Input
							placeholder="Search by name, title, or company..."
							value={vaultQuery}
							onChange={(e) => setVaultQuery(e.target.value)}
						/>
						{isSearching && (
							<div className="flex items-center justify-center py-4">
								<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
							</div>
						)}
						{!isSearching && vaultResults.length > 0 && (
							<div className="space-y-2 max-h-64 overflow-y-auto">
								{vaultResults.map((result) => (
									<div key={result.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50">
										<div className="flex items-center gap-3 min-w-0">
											{result.photoUrl ? (
												<img src={result.photoUrl} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
											) : (
												<div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0 text-xs font-medium">
													{result.fullName.charAt(0).toUpperCase()}
												</div>
											)}
											<div className="min-w-0">
												<p className="text-sm font-medium truncate">{result.fullName}</p>
												{(result.currentTitle || result.currentCompany) && (
													<p className="text-xs text-muted-foreground truncate">
														{[result.currentTitle, result.currentCompany].filter(Boolean).join(' at ')}
													</p>
												)}
												{result.location && (
													<p className="text-xs text-muted-foreground truncate">📍 {result.location}</p>
												)}
											</div>
										</div>
										<Button
											size="sm"
											variant="outline"
											onClick={() => addFromVaultMutation.mutate(result.signalhireId)}
											disabled={addFromVaultMutation.isPending}
										>
											Add
										</Button>
									</div>
								))}
							</div>
						)}
						{!isSearching && vaultQuery.length >= 2 && vaultResults.length === 0 && (
							<p className="text-sm text-muted-foreground text-center py-4">
								No candidates found. Try LinkedIn lookup or manual add.
							</p>
						)}
					</TabsContent>

					{/* LinkedIn Tab */}
					<TabsContent value="linkedin" className="space-y-3">
						<div className="space-y-2">
							<Label>LinkedIn Profile URL</Label>
							<div className="flex gap-2">
								<Input
									placeholder="https://www.linkedin.com/in/..."
									value={linkedinUrl}
									onChange={(e) => setLinkedinUrl(e.target.value)}
								/>
								<Button
									onClick={() => addByLinkedInMutation.mutate()}
									disabled={!linkedinUrl.includes('linkedin.com') || addByLinkedInMutation.isPending}
								>
									{addByLinkedInMutation.isPending ? (
										<Loader2 className="h-4 w-4 animate-spin" />
									) : (
										'Look Up'
									)}
								</Button>
							</div>
							<p className="text-xs text-muted-foreground">
								The candidate will be added to the pipeline and profile lookup will run automatically.
							</p>
						</div>
					</TabsContent>

					{/* Manual Tab */}
					<TabsContent value="manual" className="space-y-3">
						<div className="grid gap-3">
							<div>
								<Label>Name <span className="text-destructive">*</span></Label>
								<Input
									placeholder="Full name"
									value={manualForm.name}
									onChange={(e) => setManualForm(f => ({ ...f, name: e.target.value }))}
								/>
							</div>
							<div className="grid grid-cols-2 gap-3">
								<div>
									<Label>Title</Label>
									<Input
										placeholder="Job title"
										value={manualForm.currentTitle}
										onChange={(e) => setManualForm(f => ({ ...f, currentTitle: e.target.value }))}
									/>
								</div>
								<div>
									<Label>Company</Label>
									<Input
										placeholder="Company name"
										value={manualForm.currentCompany}
										onChange={(e) => setManualForm(f => ({ ...f, currentCompany: e.target.value }))}
									/>
								</div>
							</div>
							<div>
								<Label>Location</Label>
								<Input
									placeholder="City, Country"
									value={manualForm.location}
									onChange={(e) => setManualForm(f => ({ ...f, location: e.target.value }))}
								/>
							</div>
							<div className="grid grid-cols-2 gap-3">
								<div>
									<Label>Email</Label>
									<Input
										type="email"
										placeholder="email@example.com"
										value={manualForm.email}
										onChange={(e) => setManualForm(f => ({ ...f, email: e.target.value }))}
									/>
								</div>
								<div>
									<Label>Phone</Label>
									<Input
										placeholder="+62..."
										value={manualForm.phone}
										onChange={(e) => setManualForm(f => ({ ...f, phone: e.target.value }))}
									/>
								</div>
							</div>
							<div>
								<Label>LinkedIn URL</Label>
								<Input
									placeholder="https://www.linkedin.com/in/..."
									value={manualForm.linkedinUrl}
									onChange={(e) => setManualForm(f => ({ ...f, linkedinUrl: e.target.value }))}
								/>
							</div>
						</div>
						<Button
							className="w-full"
							onClick={() => addManualMutation.mutate()}
							disabled={!manualForm.name.trim() || addManualMutation.isPending}
						>
							{addManualMutation.isPending ? (
								<><Loader2 className="h-4 w-4 animate-spin mr-2" /> Adding...</>
							) : (
								<><UserPlus className="h-4 w-4 mr-2" /> Add Candidate</>
							)}
						</Button>
					</TabsContent>
				</Tabs>
			</CardContent>
		</Card>
	);
}
